import { geometryCost } from './geometry-cost';
import { generateClipsGLB } from '../../export/MotionExport';
import type { SceneObject, SceneObjectData } from '../../exports/scene';
import { getBuiltInSrc, createPrimitiveWithMaterial } from '../../exports/scene';
import type { PrimitiveType } from '../../exports/scene';
import { Model, Renderer } from '../../exports/behaviors';
import type {
    SpatialObjectDescriptor, SpatialInteractionDescriptor, SpatialBuildStats,
} from './spatial-types';
import { SCENE_OBJECT_BUDGET } from './spatial-types';
import { inspectorData } from './native-inspector';
import { interactionFromInput, spatialInteractionActions, validateSpatialInteractions } from './interaction-input';
import { applyTransform, resolveUniqueByName, wiringRefusalText } from './spatial-helpers';
import type { BehaviorGraphData } from '../../exports/behaviors';
import { foldRendererOverrides } from './spatial-material';
import { describeEnvironment, type SpatialEnvironmentDescriptor } from './spatial-environment';
import type { SpatialBuildContext } from './spatial-context';
import type { CreateChildEntry } from '../../exports/editor';
import { assertAuthoredGraph, clockGraph, compileInteraction, composeInteraction, normalizeAuthoredText, CompositeCommand, CreateObjectCommand, getUndoManager, isDocumentPlacement, isEngineRoot, topLevelParent } from '../../exports/editor';
import { planBakes } from './spatial-bake';
import { embedMeshSource } from './mesh-source';
import { rememberGeometrySource } from './geometry-source';
import { boundsNote, topLevelObjects, worldBounds } from './spatial-bounds';
import { seatBuild } from './spatial-seat';
import { fitToCards } from './spatial-cards';

// ── Topological Sort ────────────────────────────────────

function topologicalSort(descriptors: SpatialObjectDescriptor[]): SpatialObjectDescriptor[] {
    const byId = new Map<string, SpatialObjectDescriptor>();
    for (const d of descriptors) byId.set(d.id, d);

    const sorted: SpatialObjectDescriptor[] = [];
    const visited = new Set<string>();

    function visit(desc: SpatialObjectDescriptor): void {
        if (visited.has(desc.id)) return;
        visited.add(desc.id);
        // Visit parent first
        if (desc.parentId) {
            const parent = byId.get(desc.parentId);
            if (parent) visit(parent);
        }
        sorted.push(desc);
    }

    for (const d of descriptors) visit(d);
    return sorted;
}

// ── Color Normalization ─────────────────────────────────

function normalizeColor(hex: string): string {
    const trimmed = hex.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed + 'FF';
    if (/^#[0-9A-Fa-f]{8}$/.test(trimmed)) return trimmed;
    return '#CCCCCCFF'; // fallback to default gray
}

// ── Name truncation ─────────────────────────────────────

/** No ellipsis: the name is retyped in later calls, and an ellipsis comes back as three dots. */
function truncateAtWordBoundary(input: string, maxLen: number): string {
    const trimmed = input.trim();
    if (trimmed.length <= maxLen) return trimmed;
    const hardCut = trimmed.slice(0, maxLen);
    const boundaryZone = Math.floor(maxLen * 0.8);
    const lastSpace = hardCut.lastIndexOf(' ');
    return (lastSpace >= boundaryZone ? hardCut.slice(0, lastSpace) : hardCut).trimEnd();
}

// ── Dead bindings ───────────────────────────────────────

/** Diagnostics for a binding that can never take a value, so its field reads as unset (0 on a transform). */
const DEAD_BINDING_CODES = new Set(['missing-property', 'invalid-expression', 'evaluation-error', 'nested-braces']);

function deadBindingNote(objects: readonly SceneObject[]): string {
    const dead = objects.flatMap(object => object.getBehaviors().flatMap(behavior => behavior.getBindingDiagnostics()))
        .filter(diagnostic => DEAD_BINDING_CODES.has(diagnostic.code));
    if (!dead.length) return '';
    const listed = dead.slice(0, 5).map(d => `"${d.object.slice(d.object.lastIndexOf('/') + 1)}" ${d.behavior} ${d.field.slice(2)} ${JSON.stringify(d.source)}: ${d.message}`);
    const more = dead.length > 5 ? `; and ${dead.length - 5} more` : '';
    return `\n\nDead bindings: each never takes a value, so its field reads as unset (0 on a transform). ${listed.join('; ')}${more}.`;
}

// ── Materialization ─────────────────────────────────────

const PRIMITIVE_TEMPLATES = ['cube', 'sphere', 'plane', 'cylinder'] as const;

export interface MaterializeOptions {
    interactions?: SpatialInteractionDescriptor[];
    environment?: SpatialEnvironmentDescriptor;
    appendTo?: SceneObject;
    stats?: SpatialBuildStats;
    /** Objects created so far, reported about once a second while a build runs. */
    onProgress?: (created: number, total: number) => void;
}

/**
 * A build hands the event loop a turn at least this often, so timers, input
 * and paint keep running. A turn that paints a large scene can cost more than
 * that, so the build then works three times as long as its last turn took,
 * up to a second, so a host's progress display still hears from it.
 */
const YIELD_MS = 50;
const WORK_PER_TURN = 3;
const PROGRESS_MS = 1000;

function sceneTriangles(ctx: SpatialBuildContext): number {
    let triangles = 0;
    for (const mesh of ctx.traceScene.getBabylonScene().meshes) {
        if (mesh.name.startsWith('__') || !mesh.isEnabled()) continue;
        triangles += mesh.getTotalIndices() / 3;
    }
    return Math.round(triangles);
}

const SCENE_TRIANGLE_ADVISORY = 60_000;

type GroupCost = { each: number; copies: number };

const isPlainPrimitive = (d: SpatialObjectDescriptor) =>
    (PRIMITIVE_TEMPLATES as readonly string[]).includes(d.template) && !d.behaviors?.length;

function repeatedPartCosts(descriptors: SpatialObjectDescriptor[]): [string, GroupCost][] {
    const children = new Map<string, SpatialObjectDescriptor[]>();
    for (const d of descriptors) {
        if (!d.parentId) continue;
        const siblings = children.get(d.parentId);
        if (siblings) siblings.push(d);
        else children.set(d.parentId, [d]);
    }
    const round = (v?: number[]) => v?.map(n => Math.round(n * 1000) / 1000).join(',') ?? '';
    const costs = new Map<string, GroupCost & { name: string }>();
    for (const d of descriptors) {
        const kids = children.get(d.id);
        // Only what bake() folds: a group of plain primitives. A page with its
        // caption, or a part whose children carry behaviors, is not a bake.
        if (d.template === 'clone' || !kids || kids.some(k => !isPlainPrimitive(k) || children.has(k.id))) continue;
        // Its own placement is left out, so copies at different spots match.
        const key = d.template + '|' + round(d.scale) + '|' + kids.map(k => `${k.template}@${round(k.position)}|${round(k.rotation)}|${round(k.scale)}`).sort().join(';');
        const cost = costs.get(key) ?? { name: d.name, each: kids.length + 1, copies: 0 };
        cost.copies++;
        costs.set(key, cost);
    }
    return [...costs.values()].map(({ name, each, copies }) => [name, { each, copies }]);
}

/**
 * An object's colour, material and behaviors, then its active flag. A
 * behavior's load can wait on the network and on rendered frames (Text fetches
 * its font and builds its renderer), so a build runs these alongside the rest
 * of its work rather than one object after another.
 */
async function applyBehaviors(object: SceneObject, desc: SpatialObjectDescriptor): Promise<void> {
    // Colour, material and any raw Renderer behavior fold into ONE write.
    // Two `setBehavior(Renderer, …)` calls do not compose: the second
    // replaces `materials` wholesale, so a build that set both a colour and
    // a metallic factor kept only whichever landed last.
    const rendererBehavior = desc.behaviors?.find(b => b.type === 'Renderer');
    const rendererOverrides = foldRendererOverrides(
        rendererBehavior?.overrides,
        desc.color ? normalizeColor(desc.color) : undefined,
        desc.material,
    );
    if (rendererOverrides) {
        await object.setBehavior(Renderer, rendererOverrides);
    }

    // Apply behaviors (use setBehavior for idempotency — avoids errors if behavior already exists)
    if (desc.behaviors) {
        for (const beh of desc.behaviors) {
            // Renderer already went in above, folded with colour/material.
            if (beh === rendererBehavior) continue;
            const { overrides } = beh;
            // Sanitize Properties states: ensure each state has a `values` object
            // to prevent trace-viewer crash when `values` is undefined
            if (beh.type === 'Properties' && overrides?.states) {
                const states = overrides.states as Record<string, Record<string, unknown>>;
                for (const stateName of Object.keys(states)) {
                    const state = states[stateName];
                    if (state && !('values' in state)) {
                        state.values = {};
                    }
                }
            }
            // Tool input arrives double-escaped ("Line 1\\nLine 2");
            // authored data must carry real newlines — the runtime
            // renders strings verbatim.
            if (beh.type === 'Text' && typeof overrides?.text === 'string') {
                overrides.text = normalizeAuthoredText(overrides.text);
            }
            await object.setBehavior(beh.type, overrides ?? {});
        }
    }

    // Apply active state (must be after parenting so children inherit)
    if (desc.active === false) {
        object.setActive(false);
    }
}

export async function materializeSpatialBuild(
    descriptors: SpatialObjectDescriptor[],
    description: string,
    ctx: SpatialBuildContext,
    options: MaterializeOptions = {},
): Promise<string> {
    const { interactions, environment, appendTo, stats, onProgress } = options;
    validateSpatialInteractions(interactions ?? []);
    const { traceScene } = ctx;
    const trianglesBefore = sceneTriangles(ctx);
    const contentBefore = worldBounds(topLevelObjects(traceScene));

    // Resolve host-owned catalog references before creating any objects.
    const hasAssets = descriptors.some(d => d.template === 'asset');
    const resolvedAssets = new Map<string, string>();
    if (hasAssets) {
        if (!ctx.resolveAsset || !ctx.importAsset) throw new Error('Asset builds require resolveAsset and importAsset host capabilities.');
        for (const desc of descriptors) {
            if (desc.template !== 'asset' || !desc.assetId || resolvedAssets.has(desc.assetId)) continue;
            const asset = await ctx.resolveAsset(desc.assetId);
            if (!asset) throw new Error(`Catalog asset "${desc.assetId}" not found. The asset ID may be wrong — use search_catalog to find valid IDs.`);
            resolvedAssets.set(desc.assetId, asset);
        }
    }

    // Assets this build bakes and registers (clips, svg drawings), removed again if the build fails.
    const bakedSrcs: string[] = [];

    // Freeze each source subtree BEFORE creating anything. Resolve by identity,
    // not child name: different branches commonly contain identically named parts.
    // Expanded descriptors participate in parenting, rollback, undo and reporting
    // exactly like directly authored objects. Never recursively re-expand them.
    // A baked group is one object: its descendants fold into its mesh and are
    // never created, so they are out of the budget and out of every pass below.
    const bakes = planBakes(descriptors, interactions);
    if (bakes.code.size && (!ctx.executor || !ctx.addBakedMeshObject)) {
        throw new Error('Baked builds require executor and addBakedMeshObject host capabilities.');
    }
    if (bakes.code.size) {
        descriptors = descriptors.filter(d => !bakes.folded.has(d.id))
            .map(d => bakes.code.has(d.id) ? { ...d, color: undefined, material: undefined } : d);
    }
    const existingCount = traceScene.getSceneObjects().filter(o => !isEngineRoot(o) && o !== topLevelParent(traceScene)).length;
    const wrapperCount = !appendTo && descriptors.some(d => !d.parentId && !d.parentObjectName) ? 1 : 0;
    const budget = stats?.budget === null ? Infinity : stats?.budget ?? SCENE_OBJECT_BUDGET;
    let requestedCount = descriptors.length;
    const checkBudget = () => {
        if (existingCount + requestedCount + wrapperCount > budget) {
            throw new Error(`Scene object budget of ${budget} exceeded: ${existingCount} existing + ${requestedCount} requested (including cloned descendants) + ${wrapperCount} wrapper. No objects created. Use fewer copies, or bake reusable detail into one mesh.`);
        }
    };
    checkBudget();
    const cloneData = new Map<string, SceneObjectData>();
    // Objects per copy and copies, by clone source: a group cloned many times
    // multiplies its subtree, which the model cannot see from the total alone.
    const cloneCosts = new Map<string, GroupCost>();
    const repeatedCosts = repeatedPartCosts(descriptors);
    const expanded: SpatialObjectDescriptor[] = [];
    for (const desc of descriptors) {
        expanded.push(desc);
        if (desc.template !== 'clone' || !desc.sourceObjectName) continue;
        const source = resolveUniqueByName(traceScene, desc.sourceObjectName, 'clone source');
        const subtree = [source, ...source.getDescendants()];
        requestedCount += subtree.length - 1;
        checkBudget();
        // A scene reference reloads its own derived subtree from src. Recreating
        // those children as authored objects both duplicates content and violates
        // the viewer's reference-parenting boundary. Keep the placement itself.
        // Budget above still includes its loaded descendants.
        const included = new Set<SceneObject>();
        const authored = subtree.filter(object => {
            if (object !== source) {
                const parent = object.getParent();
                if (!parent || !included.has(parent) || parent.isSceneReference) return false;
            }
            included.add(object);
            return true;
        });
        const cost = cloneCosts.get(source.name) ?? { each: authored.length, copies: 0 };
        cost.copies++;
        cloneCosts.set(source.name, cost);
        const ids = new Map(authored.map((object, i) => [object, i === 0 ? desc.id : `${desc.id}/child${i}`]));
        for (const object of authored) {
            object.captureTransform();
            object.refreshSceneData();
            const id = ids.get(object)!;
            const data = object.getSceneObjectData();
            // A copy is placed to be seen: hiding a prototype (a parts
            // library) does not hide its copies. setActive(copy, false) still does.
            cloneData.set(id, object === source && data.active === false ? { ...data, active: true } : data);
            if (object === source) continue;
            const parentId = ids.get(object.getParent()!);
            if (!parentId) throw new Error(`Clone source "${source.name}" has an unresolved descendant parent.`);
            expanded.push({ id, name: object.name, template: 'clone', parentId,
                instanceGroup: desc.instanceGroup });
        }
    }
    descriptors = expanded;
    // Create root group. Descriptions can be long and share common prefixes,
    // so truncate at a word boundary and keep the full first ~40 chars.
    // Lazy: builds whose every top-level object parents into existing scene
    // content (slot fills) never need it — creating it anyway leaves an empty
    // orphan group at the scene root.
    // "/" would split the name into path segments.
    const rootName = truncateAtWordBoundary((description || 'SpatialBuild').replaceAll('/', '-'), 50);
    // Top-level authored objects belong under the document root.
    const sceneRoot = topLevelParent(traceScene);
    // Appending: the build's top-level objects go under an object that already
    // exists, and no new root group is created. Resolved BEFORE anything is
    // built so a bad target is an error, not a half-built orphan group.
    const appendTarget = appendTo ?? null;
    if (appendTarget && (appendTarget.traceScene !== traceScene || appendTarget.transform.isDisposed())) {
        throw new Error('appendTo must be a live object in this scene.');
    }
    let rootObject: SceneObject | null = null;
    const rollbackObjects = new Set<SceneObject>();
    const trackCreated = (object: SceneObject): void => { rollbackObjects.add(object); };
    // Viewer create APIs allocate/register their root synchronously, then await
    // reload (assets, fonts, behaviors). Capture that allocation BEFORE awaiting:
    // a rejected reload never returns its root to the caller. This narrow window
    // cannot collect unrelated user edits made while the load is in flight.
    const createTracked = async (create: () => Promise<SceneObject>): Promise<SceneObject> => {
        const before = new Set(traceScene.getSceneObjects());
        let pending: Promise<SceneObject>;
        try { pending = create(); }
        finally {
            for (const object of traceScene.getSceneObjects()) {
                if (!before.has(object)) rollbackObjects.add(object);
            }
        }
        const object = await pending;
        rollbackObjects.add(object);
        return object;
    };
    const getRootObject = async (): Promise<SceneObject> => {
        if (appendTarget) return appendTarget;
        rootObject ??= await createTracked(() => traceScene.createSceneObject(rootName, sceneRoot ?? undefined));
        return rootObject;
    };

    // Sort descriptors (parents before children)
    const sorted = topologicalSort(descriptors);

    // Map sandbox IDs to created SceneObjects
    const idToObject = new Map<string, SceneObject>();
    const pendingImageStart = ctx.pendingImages?.length ?? 0;

    // Cache name lookups so N children of the same existing parent resolve once.
    const existingObjects = new Map<string, SceneObject>();
    const resolveExisting = (name: string, role: string): SceneObject => {
        const cached = existingObjects.get(name);
        if (cached) return cached;
        const found = resolveUniqueByName(traceScene, name, role);
        existingObjects.set(name, found);
        return found;
    };

    // Identical baked parts share one code string: bake it once, then copy that
    // object like a clone, so every copy shares one asset.
    const bakedSources = new Map<string, SceneObject>();
    const bakedPart = async (desc: SpatialObjectDescriptor, parent: SceneObject): Promise<SceneObject> => {
        const code = bakes.code.get(desc.id)!;
        const first = bakedSources.get(code);
        if (first) {
            if (ctx.cloneBakedMeshObject) return ctx.cloneBakedMeshObject(first, desc.name, parent, trackCreated);
            const data = first.getSceneObjectData();
            return traceScene.createSceneObjectFromData({ ...data, name: desc.name, transform: { ...data.transform, parent: parent.name } }, parent);
        }
        const assetName = desc.name.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_') || 'part';
        const built = await ctx.executor!.mesh(code, traceScene.getBabylonScene(), assetName);
        if (!built.glb || !built.stats) throw new Error(`bake("${desc.name}"): ${built.errors.join('; ')}`);
        const source = { code, description: `bake() of "${desc.name}": its primitives merged into one mesh.`, stats: built.stats };
        const object = await ctx.addBakedMeshObject!(await embedMeshSource(built.glb, source), desc.name, assetName, parent, trackCreated);
        rememberGeometrySource(traceScene, object, source);
        bakedSources.set(code, object);
        return object;
    };

    let lastYield = performance.now();
    let lastProgress = lastYield;
    let workMs = YIELD_MS;
    // Each load records its error rather than rejecting unobserved; the build
    // throws the first one at its next turn or when every object exists.
    const behaviorLoads: Promise<void>[] = [];
    const loadFailure: { error?: unknown } = {};
    try {
    // Clips the blocks asked for (CSS keyframes) are baked and registered before anything plays them; svg drawings too, one GLB per
    // distinct drawing. Inside the try, so a failure anywhere in the build removes them.
    for (const desc of descriptors) {
        for (const asset of desc.clips ?? []) {
            if (traceScene.resources.getAsset(asset.src)) continue;
            const glb = await generateClipsGLB(asset.clips.map(c => ({ name: c.name, keyframes: c.keyframes, fps: asset.fps })), traceScene.getBabylonScene());
            traceScene.resources.addAsset(asset.src, new Blob([new Uint8Array(await glb.arrayBuffer())], { type: 'model/gltf-binary' }));
            bakedSrcs.push(asset.src);
        }
        if (desc.svg && !traceScene.resources.getAsset(desc.svg.src)) {
            const { generateSvgGLB } = await import('./html-svg-glb');
            const glb = await generateSvgGLB(desc.svg.pieces, desc.svg.aspect, traceScene.getBabylonScene());
            traceScene.resources.addAsset(desc.svg.src, new Blob([new Uint8Array(await glb.arrayBuffer())], { type: 'model/gltf-binary' }));
            bakedSrcs.push(desc.svg.src);
        }
    }
    for (const [index, desc] of sorted.entries()) {
        // Creating a primitive can await only microtasks, so without this a
        // build of a thousand objects holds the event loop for minutes.
        if (performance.now() - lastYield >= workMs) {
            const turnStart = performance.now();
            await new Promise(resolve => setTimeout(resolve, 0));
            workMs = Math.min(PROGRESS_MS, Math.max(YIELD_MS, WORK_PER_TURN * (performance.now() - turnStart)));
            // Input ran during the turn. Every parent already holds an object
            // of this build (its first child precedes the first yield), so
            // checking the build's objects also catches a deleted parent.
            if (traceScene.getBabylonScene().isDisposed || [...idToObject.values()].some(o => o.transform.isDisposed())) {
                throw new Error('The scene changed during the build (an object it created or builds into was deleted). Build rolled back.');
            }
            if ('error' in loadFailure) throw loadFailure.error;
            lastYield = performance.now();
            if (lastYield - lastProgress >= PROGRESS_MS) {
                lastProgress = lastYield;
                try { onProgress?.(index, sorted.length); } catch { /* a host's progress display never fails the build */ }
            }
        }
        // Parent resolution, in order: a handle from this build, a pre-existing
        // scene object referenced by name, else the build's root group.
        const parentSceneObj = desc.parentId
            ? idToObject.get(desc.parentId) ?? await getRootObject()
            : desc.parentObjectName
                ? resolveExisting(desc.parentObjectName, 'parent')
                : await getRootObject();

        let newObj: SceneObject;

        if (bakes.code.has(desc.id)) {
            newObj = await createTracked(() => bakedPart(desc, parentSceneObj));
            traceScene.reparentSceneObject(newObj, parentSceneObj);
        } else if (desc.template === 'clone') {
            const sourceData = cloneData.get(desc.id)!;
            newObj = await createTracked(() => traceScene.createSceneObjectFromData({
                ...sourceData,
                name: desc.name,
                transform: { ...sourceData.transform, parent: parentSceneObj.name },
            }, parentSceneObj));
        } else if (desc.template === 'asset' && desc.assetId) {
            newObj = await createTracked(() => ctx.importAsset!(resolvedAssets.get(desc.assetId!)!, desc.name, trackCreated));
            rollbackObjects.add(newObj);

            // Add Renderer if not already present — never on a document
            // placement, whose geometry belongs to the asset's root below it.
            if (!isDocumentPlacement(newObj) && !newObj.getBehavior(Renderer)) {
                await newObj.addBehavior(Renderer);
            }

            // Reparent under the correct parent within the build
            traceScene.reparentSceneObject(newObj, parentSceneObj);
        } else {
            // Primitive or empty
            const isPrimitive = (PRIMITIVE_TEMPLATES as readonly string[]).includes(desc.template);
            const primitiveSrc = isPrimitive ? getBuiltInSrc(desc.template as PrimitiveType) : undefined;
            newObj = await createTracked(() => traceScene.createSceneObject(desc.name, parentSceneObj, primitiveSrc ?? desc.svg?.src));
            // An svg's own file is played by a Model; model-authored builds cannot ask for one, so it is added here.
            if (desc.svg) await newObj.addBehavior(Model);

            if (isPrimitive) {
                const mesh = createPrimitiveWithMaterial(
                    desc.template as PrimitiveType,
                    newObj.babylonScene,
                    newObj.name,
                );
                mesh.parent = newObj.transform;
                await newObj.addBehavior(Renderer);
            }
        }

        // Track immediately so later transform/behavior failures roll back this object too.
        idToObject.set(desc.id, newObj);

        // The DSL describes local transforms. Empty-create preserves world
        // rotation for editor placement, so undo that compensation for new DSL
        // primitives/groups when rotate() was omitted. Clones/assets keep their
        // existing authored orientation unless explicitly overridden.
        const rotation = desc.rotation ?? (desc.template !== 'clone' && desc.template !== 'asset' ? [0, 0, 0] as [number, number, number] : undefined);
        applyTransform(newObj.transform, desc.position, rotation, desc.scale);
        // Properties primes its baseline from authored scene data, not the
        // transient Babylon pose. Commit placement before a partial binding
        // (e.g. rotation only) can restore the other components to the origin.
        newObj.captureTransform();

        behaviorLoads.push(applyBehaviors(newObj, desc).catch((error: unknown) => { if (!('error' in loadFailure)) loadFailure.error = error; }));

        // Declarative image: queue for parallel generation after the loop.
        if (desc.imagePrompt) {
            (ctx.pendingImages ??= []).push({ object: newObj, prompt: desc.imagePrompt });
        }
    }

    await Promise.all(behaviorLoads);
    if ('error' in loadFailure) throw loadFailure.error;

    // Referenced assets may have changed since the source snapshot was loaded.
    // Verify actual loaded size before committing the build or its undo record.
    const loadedCount = traceScene.getSceneObjects().filter(o => !isEngineRoot(o) && o !== sceneRoot).length;
    if (loadedCount > budget) {
        throw new Error(`Scene object budget of ${budget} exceeded after loading referenced content (${loadedCount} objects). Build rolled back. Use fewer copies or smaller reusable assets.`);
    }

    // Resolve staged handles after creation, then store ordinary relative paths.
    for (const desc of sorted) {
        if (!desc.inspector) continue;
        if (desc.behaviors?.some(b => b.type === 'AutoUI')) throw new Error('Use inspector or addBehavior(AutoUI), not both on the same owner');
        const owner = idToObject.get(desc.id)!, focus = idToObject.get(desc.inspector.focusId);
        if (!focus) throw new Error('Inspector focus handle not found');
        const data = inspectorData(owner, focus, desc.inspector.options);
        await owner.setBehavior('AutoUI', { ...data });
    }

    // Validate and compose every interaction before attaching any graphs. Fail the
    // build on invalid wiring instead of returning silently broken interactions.
    const stagedGraphs = new Map<SceneObject, BehaviorGraphData>();
    for (const interaction of interactions ?? []) {
        const actions = spatialInteractionActions(interaction);
        const resolved = interactionFromInput({ object: interaction.sourceId, event: interaction.event ?? 'select', scope: interaction.scopeId, when: interaction.when, actions },
            ref => idToObject.get(ref) ?? null,
            // An action may drive an object already in the scene (a deck a button pages).
            ref => idToObject.get(ref) ?? resolveExisting(ref, 'interaction target'));
        const compiled = compileInteraction(resolved);
        if ('refused' in compiled) throw new Error(wiringRefusalText(compiled.refused));
        const existing = stagedGraphs.get(resolved.object) ?? resolved.object.getBehavior('BehaviorGraph')?.getData() as BehaviorGraphData | undefined;
        const composed = composeInteraction(existing, compiled.graph);
        if ('conflicts' in composed) throw new Error(`event-conflict: "${resolved.object.name}" already handles ${composed.conflicts.join(', ')}`);
        stagedGraphs.set(resolved.object, composed.data);
    }
    for (const desc of sorted) {
        const object = idToObject.get(desc.id);
        for (const clock of object ? desc.clocks ?? [] : []) {
            const existing = stagedGraphs.get(object!) ?? object!.getBehavior('BehaviorGraph')?.getData() as BehaviorGraphData | undefined;
            const composed = composeInteraction(existing, clockGraph(object!, { property: clock.property, period: clock.period, rateScale: clock.rateScale }));
            if ('conflicts' in composed) throw new Error(`event-conflict: "${object!.name}" already handles ${composed.conflicts.join(', ')}`);
            stagedGraphs.set(object!, composed.data);
        }
    }
    for (const data of stagedGraphs.values()) assertAuthoredGraph(data);
    for (const [object, data] of stagedGraphs) {
        const existing = object.getBehavior('BehaviorGraph');
        if (existing) {
            const errors = await existing.commitSnapshot(data);
            if (errors.length) throw new Error(JSON.stringify(errors));
        } else await object.setBehavior('BehaviorGraph', { ...data });
    }
    } catch (err) {
        // Loads still in flight finish before their objects are deleted.
        await Promise.all(behaviorLoads);
        ctx.pendingImages?.splice(pendingImageStart);
        for (const src of bakedSrcs) traceScene.resources.removeAsset(src);
        // Unwind the partial build — half-built groups otherwise linger as
        // invisible orphans the model then confuses for real content.
        for (const so of [...rollbackObjects].reverse()) {
            try { traceScene.deleteSceneObject(so); } catch { /* already gone */ }
        }
        if (rootObject) {
            try { traceScene.deleteSceneObject(rootObject); } catch { /* already gone */ }
        }
        throw err;
    }

    // A build may be a forest: appended copies and slot fills can have different
    // existing parents, even when other objects use a new wrapper. Each create
    // command must own a real subtree; treating siblings as its children leaves
    // them behind on undo. The existing Composite codec keeps this one replayable
    // user action without inserting an artificial parent into the scene.
    const createdInOrder = sorted.map(d => idToObject.get(d.id)).filter((o): o is SceneObject => !!o);
    const created = new Set(rootObject ? [rootObject, ...createdInOrder] : createdInOrder);
    const roots = [...created].filter(object => !created.has(object.getParent()!));
    // Before the create commands record the objects, so undo and redo hold the seated poses.
    const seatNote = await seatBuild(sorted, idToObject, ref => resolveExisting(ref, 'seat'), roots);
    const cardNote = fitToCards(sorted.filter(d => d.card).map(d => idToObject.get(d.id)!), created);
    const commands: CreateObjectCommand[] = [];
    if (!getUndoManager().isUndoRedoing) {
        for (const root of roots) {
            const childEntries: CreateChildEntry[] = [];
            // getDescendants is parent-first; exclude loader-owned reference
            // content, which reloads from src on redo just as on first creation.
            for (const child of root.getDescendants()) {
                if (!created.has(child)) continue;
                child.captureTransform();
                child.refreshSceneData();
                childEntries.push({ object: child, data: child.getSceneObjectData(), parent: child.getParent() });
            }
            root.captureTransform();
            root.refreshSceneData();
            commands.push(new CreateObjectCommand(root, root.getParent(), root.getSceneObjectData(), childEntries));
        }
        if (commands.length) {
            getUndoManager().push(commands.length === 1 ? commands[0] : new CompositeCommand(`Build ${rootName}`, commands));
        }
    }
    if (roots.length) traceScene.select(rootObject ?? roots[0]);
    ctx.onSceneChanged?.();

    // Build a summary so the AI knows exactly what was created
    const typeCounts = new Map<string, number>();
    for (const d of descriptors) {
        typeCounts.set(d.template, (typeCounts.get(d.template) ?? 0) + 1);
    }
    const breakdown = Array.from(typeCounts.entries())
        .map(([t, n]) => `${n} ${t}${n > 1 ? 's' : ''}`)
        .join(', ');

    // Count behaviors
    const behaviorCounts = new Map<string, number>();
    for (const d of descriptors) {
        if (d.behaviors) {
            for (const b of d.behaviors) {
                behaviorCounts.set(b.type, (behaviorCounts.get(b.type) ?? 0) + 1);
            }
        }
    }
    const behaviorBreakdown = behaviorCounts.size > 0
        ? '; behaviors: ' + Array.from(behaviorCounts.entries())
            .map(([t, n]) => `${n} ${t}`)
            .join(', ')
        : '';

    // Names the model can target next turn. Instance sets collapse to their
    // first member plus a count: 480 numbered columns is not information, and
    // it used to be most of this message.
    const namedGroups = new Set<string>();
    const nameList: string[] = [];
    for (const d of descriptors) {
        if (d.template === 'empty' && !d.behaviors?.length) continue;
        if (d.instanceGroup) {
            if (namedGroups.has(d.instanceGroup)) continue;
            namedGroups.add(d.instanceGroup);
            const n = descriptors.filter(o => o.instanceGroup === d.instanceGroup).length;
            nameList.push(`"${d.name}" …×${n}`);
            continue;
        }
        nameList.push(`"${d.name}"`);
    }
    const objectNames = nameList.slice(0, 30).join(', ')
        + (nameList.length > 30 ? `, +${nameList.length - 30} more` : '');

    const interactionNote = interactions && interactions.length > 0
        ? ` ${interactions.length} interaction(s) wired via BehaviorGraph.`
        : '';

    // Scene environment is authored CONFIG, not scene content: the viewer reads
    // `ConfigData.environmentImage` at scene construction, never on mutation,
    // so the host that builds the page applies it. Carried on the context for
    // the bench exporter, and reported so the model knows it was recorded
    // rather than applied to the live editor viewport.
    let environmentNote = '';
    if (environment) {
        ctx.sceneEnvironment = environment;
        environmentNote = ` Scene ${describeEnvironment(environment)} (scene config — applies wherever the scene loads, not to the editor viewport).`;
    }

    // The name the root got, which differs from `rootName` when a sibling has it already.
    const builtRoot = rootObject as SceneObject | null;
    // The scene root is not offered as a group for the next build.
    const appendedToRoot = !!appendTarget && appendTarget === sceneRoot;
    const anchor = appendTarget && !appendedToRoot
        ? appendTarget.name
        : builtRoot?.name ?? null;
    const grouping = appendedToRoot
        ? 'Parented at the scene root (no wrapper group).'
        : appendTarget
            ? `Appended under the existing "${appendTarget.name}".`
            : builtRoot
                ? `Grouped under "${builtRoot.name}".`
                : 'Parented into the existing objects you targeted (no wrapper group).';

    // Budget line. Counted off the live scene rather than off the descriptors,
    // so it stays true when a build appends to something built earlier.
    const sceneNow = traceScene.getSceneObjects().filter(o => !isEngineRoot(o) && o !== sceneRoot).length;
    const budgetNote = budget
        ? ` Scene now ${sceneNow}/${budget} objects (${Math.max(0, budget - sceneNow)} left).`
        : '';
    const instanceNote = stats?.instanceCalls
        ? ` ${descriptors.filter(d => d.instanceGroup).length} of them from ${stats.instanceCalls} instances() call(s).`
        : '';
    const multiplied = (costs: Iterable<[string, GroupCost]>) => [...costs]
        .filter(([, c]) => c.each > 1 && c.copies > 1 && c.copies * (c.each - 1) >= 20)
        .map(([name, c]) => `"${name}" ${c.each} objects × ${c.copies} = ${c.each * c.copies}`);
    const bakeNote = bakes.code.size
        ? ` Baked ${bakes.code.size} group${bakes.code.size === 1 ? '' : 's'} into one object each (${bakes.folded.size} primitives folded, ${bakedSources.size} distinct mesh${bakedSources.size === 1 ? '' : 'es'}).`
        : '';
    const groupLines = [['Cloned groups', multiplied(cloneCosts)], ['Repeated parts', multiplied(repeatedCosts)]] as const;
    const groupNote = groupLines.some(([, lines]) => lines.length)
        ? groupLines.filter(([, lines]) => lines.length).map(([label, lines]) => ` ${label}: ${lines.join(', ')}.`).join('')
            + ' bake(group), or one baked prototype, makes each copy of such a part one object.'
        : '';
    const trianglesNow = sceneTriangles(ctx);
    const added = trianglesNow - trianglesBefore;
    const triangleNote = ` This build added ${added.toLocaleString()} triangles; the scene draws ${trianglesNow.toLocaleString()}.`
        + (trianglesNow > SCENE_TRIANGLE_ADVISORY
            ? ` That is past ${SCENE_TRIANGLE_ADVISORY.toLocaleString()}: lower segments on repeated round parts (spheres, cylinders) or build them once as a prototype and clone it.`
            : '');
    const measuredCost = trianglesNow > SCENE_TRIANGLE_ADVISORY ? geometryCost(traceScene, rootObject ?? appendTarget ?? undefined) : null;
    const costNote = measuredCost
        ? `\nMeasured geometry cost: ${JSON.stringify(measuredCost)}\nFor repeated curved parts, build one economical prototype and clone it. Per-segment bevels can multiply geometry; preserve required shape and editability when simplifying.`
        : '';
    const addMore = anchor
        ? ` To add more, call build_spatial again with parent: "${anchor}".`
        : '';

    const referenceNote = createdInOrder.some(object => object.isSceneReference)
        ? ' Created counts authored objects; loaded reference descendants are included in the scene total.'
        : '';
    const extentNote = boundsNote(worldBounds(roots), contentBefore);
    return `Created ${descriptors.length} objects (${breakdown}${behaviorBreakdown}).`
        + `${instanceNote}${bakeNote}${groupNote} ${grouping}${budgetNote}${referenceNote}${triangleNote}${extentNote}${costNote}${addMore}${interactionNote}${environmentNote}`
        + `\n\nObject names: ${objectNames}${deadBindingNote(createdInOrder)}${seatNote}${cardNote}`;
}
